// MULTI-SPACE-ACCESS: the channel's Spaces tab (its managers; never a private channel or a DM):
// the home space it lives in, and the spaces it is also shared into, each with the level their
// members get (`channels.shares.set`). The same control as New channel's "Also share with".
import { Button, toast } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { type ShareInput, SpaceSharesField } from "../work/space-shares";
import type { ChannelDetailsTabProps } from "./channel-details";
import { channelSharesQuery, chatKeys, spacesQuery } from "./chat-queries";

export function SharingTab({ chat, channel }: ChannelDetailsTabProps) {
  const queryClient = useQueryClient();
  const spaces = useQuery(spacesQuery(chat.api, chat.co));
  const shares = useQuery(channelSharesQuery(chat.api, chat.co));
  const saved = JSON.stringify(
    (shares.data?.data ?? [])
      .filter((s) => s.object_id === channel.id)
      .map((s) => ({ space_id: s.space_id, level: s.level })),
  );
  const [value, setValue] = useState<ShareInput[]>([]);
  useEffect(() => setValue(JSON.parse(saved) as ShareInput[]), [saved]);
  const save = useMutation({
    mutationFn: () =>
      chat.api.client.channels.shares.set({
        params: { co: chat.co, id: channel.id },
        body: { shares: value },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      toast.add({ title: "Sharing saved", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: "Could not save the sharing", description: e.message, type: "error" }),
  });
  const home = spaces.data?.data.find((s) => s.id === channel.space_id)?.name ?? "its space";
  const dirty = JSON.stringify(value) !== saved;
  return (
    <div className="flex flex-col gap-4 pt-2" data-slot="channel-sharing">
      <p className="m-0 text-sm text-muted-foreground">Lives in {home}.</p>
      <SpaceSharesField
        spaces={spaces.data?.data ?? []}
        homeSpaceId={channel.space_id ?? ""}
        value={value}
        onChange={setValue}
        what="channel"
        disabled={save.isPending || !shares.isSuccess}
      />
      {shares.isError ? (
        <p className="m-0 text-sm text-destructive">The sharing could not be loaded.</p>
      ) : null}
      <div>
        {/* Codex r1: never save over a set that has not loaded (it would drop the shares). */}
        <Button
          size="sm"
          disabled={!dirty || save.isPending || !shares.isSuccess}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
